import { useState, useCallback } from 'react'
import { newTile, OverviewTile, useDefaultOverviewTiles } from '../defaults'
import TileConfigDialog from './TileConfigDialog'
import { Button } from '@/components/ui/button'
import { Plus, X, RotateCcw } from 'lucide-react'
import { OverviewContext } from '../context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { FormValues, MAIN_FIELD, SIDEBAR_FIELD } from '..'
import Overview from './Overview'

interface Props {
  onCancel: () => void
}

const EditableOverview = ({ onCancel }: Props) => {
  const { watch, getValues, formState } = useFormContext<FormValues>()
  const mainArray = useFieldArray<FormValues>({ name: MAIN_FIELD })
  const sidebarArray = useFieldArray<FormValues>({ name: SIDEBAR_FIELD })
  const defaultTiles = useDefaultOverviewTiles()
  const isPending = formState.isSubmitting

  const [editingField, setEditingField] = useState<{
    tile: OverviewTile
    index: number
  } | null>(null)

  const mainTiles = watch(MAIN_FIELD) as OverviewTile[]
  const sidebarTiles = watch(SIDEBAR_FIELD) as OverviewTile[]

  const handleReset = useCallback(() => {
    const newMainDefaults = defaultTiles
      .filter((t) => t.position === 'main')
      .map((t) => ({
        ...t,
        id: undefined,
      }))
    const newSidebarDefaults = defaultTiles
      .filter((t) => t.position === 'sidebar')
      .map((t) => ({
        ...t,
        id: undefined,
      }))

    mainArray.replace([
      ...newMainDefaults,
      ...getValues(MAIN_FIELD)
        .filter((t) => t.id)
        .map((t) => ({ ...t, _destroy: true })),
    ])
    sidebarArray.replace([
      ...newSidebarDefaults,
      ...getValues(SIDEBAR_FIELD)
        .filter((t) => t.id)
        .map((t) => ({ ...t, _destroy: true })),
    ])
  }, [getValues, mainArray, sidebarArray])

  const handleEditTile = useCallback((tile: OverviewTile, index: number) => {
    setEditingField({ tile, index })
  }, [])

  return (
    <>
      <h2 className='flex items-center justify-between'>
        <span>Overview</span>
        <div className='flex items-center gap-2'>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm'>
                <Plus className='h-4 w-4 mr-2' />
                Add tile
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  const tile: OverviewTile = { ...newTile }
                  mainArray.prepend(tile)
                  setEditingField({ tile: { ...tile }, index: 0 })
                }}
              >
                Add to main section
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const tile: OverviewTile = { ...newTile, position: 'sidebar' }
                  sidebarArray.prepend(tile)
                  setEditingField({ tile: { ...tile }, index: 0 })
                }}
              >
                Add to sidebar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleReset}
          >
            <RotateCcw className='h-4 w-4 mr-2' />
            Reset
          </Button>
          <Button type='button' variant='outline' size='sm' onClick={onCancel}>
            <X className='h-4 w-4 mr-2' />
            Cancel
          </Button>
          <Button type='submit' size='sm' disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </h2>

      <OverviewContext.Provider
        value={{
          isCustomizing: true,
          isPending: false,
          onEditTile: handleEditTile,
        }}
      >
        <Overview
          mainTiles={mainTiles}
          sidebarTiles={sidebarTiles}
          isCustomizing={true}
        />
      </OverviewContext.Provider>

      {editingField && (
        <TileConfigDialog
          index={editingField.index}
          initialTileState={editingField.tile}
          handleClose={() => setEditingField(null)}
        />
      )}
    </>
  )
}
export default EditableOverview
